import React from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Paper
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Assessment as AssessmentIcon,
  Storage as StorageIcon,
  TrendingUp as TrendingUpIcon,
  Security as SecurityIcon,
  Public as PublicIcon,
  Speed as SpeedIcon,
  Analytics as AnalyticsIcon,
  Science as ScienceIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const AAPSystemDetail: React.FC = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate('/admin/pi-planning');
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mr: 2 }}
          aria-label="Back to PI Planning Board"
        >
          Back
        </Button>
        <Box>
          <Typography variant="h4" sx={{ color: '#003366', fontWeight: 'bold' }}>
            CBP Data Ecosystem & Advanced Analytics Platform (AAP)
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Enterprise Data Aggregation and Predictive Analytics System
          </Typography>
        </Box>
      </Box>

      {/* System Overview */}
      <Card sx={{ mb: 3, bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            System Overview
          </Typography>
          <Typography variant="body1" paragraph>
            The Advanced Analytics Platform (AAP) is CBP's enterprise data ecosystem that aggregates, integrates, and 
            analyzes data from across the agency to provide actionable insights for field operations, trade analysis, 
            and strategic decision-making. AAP combines big data infrastructure, machine learning capabilities, and 
            advanced visualization tools to transform raw operational data into intelligence that enhances border 
            security, trade facilitation, and resource optimization.
          </Typography>
        </CardContent>
      </Card>

      {/* 5Ws Summary */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <AssessmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        System Overview: The 5Ws
      </Typography>

      <Card sx={{ mb: 4 }}>
        <TableContainer>
          <Table aria-label="AAP System 5Ws Overview">
            <TableBody>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ width: '15%', bgcolor: '#D32F2F', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="What is AAP"
                >
                  What
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    An <strong>enterprise data analytics platform</strong> that consolidates data from 50+ CBP systems 
                    including TECS, ACE, ATS, IDENT, and operational databases. AAP provides <strong>big data processing, 
                    machine learning models, and advanced visualization</strong> to support targeting, risk assessment, 
                    trade analysis, and operational intelligence across CBP's mission areas.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#1976D2', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="Who uses AAP"
                >
                  Who
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    Operated by <strong>CBP Office of Information and Technology (OIT)</strong> and used by data analysts, 
                    targeting specialists, trade analysts, and leadership across CBP. Primary users include <strong>National 
                    Targeting Center (NTC), Office of Field Operations (OFO), Border Patrol, and Office of Trade</strong>. 
                    Supports over <strong>5,000 analysts and decision-makers</strong> agency-wide.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#2E7D32', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="When was AAP deployed"
                >
                  When
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    Initial deployment in <strong>2016</strong> as part of CBP's data modernization initiative. Enhanced with 
                    <strong> Hadoop/Spark infrastructure (2017)</strong>, machine learning capabilities (2018-2020), and 
                    <strong> cloud migration (2021-present)</strong>. Continuously updated with new data sources and analytical 
                    models. Operates <strong>24/7</strong> processing billions of records daily.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#F57C00', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="Where is AAP deployed"
                >
                  Where
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    <strong>Hybrid cloud infrastructure</strong> with on-premises data centers and AWS GovCloud deployment. 
                    Accessible to authorized users <strong>nationwide through secure networks</strong>. Data processing occurs 
                    in <strong>distributed computing clusters</strong> with visualization tools available via web interfaces. 
                    Supports both <strong>centralized analytics and edge computing</strong> for field operations.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#7B1FA2', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="Why AAP exists"
                >
                  Why
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    To provide <strong>data-driven intelligence</strong> that enhances CBP's ability to identify threats, 
                    optimize resources, and improve operational efficiency. Enables <strong>predictive analytics</strong> for 
                    targeting, trade pattern analysis for enforcement, and <strong>performance metrics</strong> for leadership 
                    decision-making. Critical for transforming CBP from reactive to <strong>proactive, intelligence-driven operations</strong>.
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Core Capabilities */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <AnalyticsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Core Platform Capabilities
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <StorageIcon sx={{ fontSize: 40, color: '#2E7D32', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                  Enterprise Data Aggregation
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Consolidation and integration of data from 50+ CBP operational systems.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Data Lake Architecture:</strong> Petabyte-scale storage for structured/unstructured data</Typography></li>
                <li><Typography variant="body2"><strong>ETL Pipelines:</strong> Automated data extraction, transformation, and loading</Typography></li>
                <li><Typography variant="body2"><strong>Data Quality:</strong> Validation, cleansing, and standardization processes</Typography></li>
                <li><Typography variant="body2"><strong>Master Data Management:</strong> Entity resolution and unified identifiers</Typography></li>
                <li><Typography variant="body2"><strong>Real-Time Streaming:</strong> Kafka-based event processing for operational data</Typography></li>
                <li><Typography variant="body2"><strong>Historical Archives:</strong> Multi-year data retention for trend analysis</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #1976D2' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ScienceIcon sx={{ fontSize: 40, color: '#1976D2', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                  Predictive Modeling & Analytics
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Machine learning and statistical models for risk assessment and forecasting.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Risk Scoring Models:</strong> ML-based traveler and cargo risk assessment</Typography></li>
                <li><Typography variant="body2"><strong>Anomaly Detection:</strong> Identification of unusual patterns and behaviors</Typography></li>
                <li><Typography variant="body2"><strong>Predictive Targeting:</strong> Forecasting high-risk shipments and travelers</Typography></li>
                <li><Typography variant="body2"><strong>Network Analysis:</strong> Entity relationship mapping and link analysis</Typography></li>
                <li><Typography variant="body2"><strong>Time Series Forecasting:</strong> Volume and resource demand prediction</Typography></li>
                <li><Typography variant="body2"><strong>Model Management:</strong> MLOps for model deployment and monitoring</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #F57C00' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TrendingUpIcon sx={{ fontSize: 40, color: '#F57C00', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#F57C00', fontWeight: 'bold' }}>
                  Field Operations Insights
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Operational intelligence and performance analytics for frontline personnel.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Officer Performance:</strong> Inspection efficiency and effectiveness metrics</Typography></li>
                <li><Typography variant="body2"><strong>Port Analytics:</strong> Wait times, throughput, and resource utilization</Typography></li>
                <li><Typography variant="body2"><strong>Seizure Analysis:</strong> Contraband detection patterns and trends</Typography></li>
                <li><Typography variant="body2"><strong>Staffing Optimization:</strong> Workload forecasting and shift planning</Typography></li>
                <li><Typography variant="body2"><strong>Equipment Utilization:</strong> NII, canine, and technology deployment analysis</Typography></li>
                <li><Typography variant="body2"><strong>Real-Time Dashboards:</strong> Live operational status and alerts</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #D32F2F' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AssessmentIcon sx={{ fontSize: 40, color: '#D32F2F', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#D32F2F', fontWeight: 'bold' }}>
                  Trade Pattern Analysis
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Advanced analytics for trade compliance and enforcement operations.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Trade Flow Analysis:</strong> Import/export volume and value trends</Typography></li>
                <li><Typography variant="body2"><strong>Fraud Detection:</strong> Undervaluation, misclassification, and evasion patterns</Typography></li>
                <li><Typography variant="body2"><strong>Supply Chain Mapping:</strong> Importer-exporter-manufacturer relationships</Typography></li>
                <li><Typography variant="body2"><strong>Revenue Analytics:</strong> Duty collection and trade remedy enforcement</Typography></li>
                <li><Typography variant="body2"><strong>Compliance Scoring:</strong> Importer risk profiles and audit prioritization</Typography></li>
                <li><Typography variant="body2"><strong>Economic Impact:</strong> Trade policy and enforcement outcome analysis</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Platform Architecture */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <StorageIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Platform Architecture & Technology Stack
      </Typography>

      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#003366' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Component</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Technology</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Purpose</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell><strong>Data Storage</strong></TableCell>
              <TableCell>Hadoop HDFS, AWS S3, PostgreSQL</TableCell>
              <TableCell>Distributed file system and relational databases</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Processing Engine</strong></TableCell>
              <TableCell>Apache Spark, Flink</TableCell>
              <TableCell>Batch and stream processing at scale</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>Machine Learning</strong></TableCell>
              <TableCell>TensorFlow, PyTorch, Scikit-learn</TableCell>
              <TableCell>Model development and training</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Data Pipeline</strong></TableCell>
              <TableCell>Apache Kafka, Airflow, NiFi</TableCell>
              <TableCell>Event streaming and workflow orchestration</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>Analytics Query</strong></TableCell>
              <TableCell>Presto, Hive, Elasticsearch</TableCell>
              <TableCell>Interactive SQL and search capabilities</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Visualization</strong></TableCell>
              <TableCell>Tableau, Power BI, Custom Dashboards</TableCell>
              <TableCell>Business intelligence and reporting</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>Security</strong></TableCell>
              <TableCell>Kerberos, Ranger, Knox</TableCell>
              <TableCell>Authentication, authorization, and encryption</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Cloud Platform</strong></TableCell>
              <TableCell>AWS GovCloud</TableCell>
              <TableCell>Scalable cloud infrastructure</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* System Scale */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <PublicIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        System Scale & Performance
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                5+ PB
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Data Storage
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Petabytes of operational data
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #1976D2' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                50+
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Data Sources
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Integrated CBP systems
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #F57C00' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#F57C00', fontWeight: 'bold' }}>
                5K+
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active Users
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Analysts and decision-makers
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #D32F2F' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#D32F2F', fontWeight: 'bold' }}>
                100+
              </Typography>
              <Typography variant="body2" color="text.secondary">
                ML Models
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Production analytics models
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Key Benefits */}
      <Card sx={{ mb: 4, bgcolor: '#e3f2fd', border: '2px solid #1976D2' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#1976D2' }}>
            Operational Benefits
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Enhanced Targeting:</strong> ML models improve threat detection accuracy</Typography></li>
                <li><Typography variant="body2"><strong>Resource Optimization:</strong> Data-driven staffing and deployment decisions</Typography></li>
                <li><Typography variant="body2"><strong>Faster Insights:</strong> Real-time analytics vs. manual reporting</Typography></li>
                <li><Typography variant="body2"><strong>Proactive Operations:</strong> Predictive capabilities enable prevention</Typography></li>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Trade Enforcement:</strong> Advanced fraud detection and compliance analytics</Typography></li>
                <li><Typography variant="body2"><strong>Performance Metrics:</strong> Objective measurement of operational effectiveness</Typography></li>
                <li><Typography variant="body2"><strong>Strategic Planning:</strong> Data-driven policy and investment decisions</Typography></li>
                <li><Typography variant="body2"><strong>Interoperability:</strong> Unified view across disparate CBP systems</Typography></li>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Use Cases */}
      <Card sx={{ mb: 4, bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            Key Use Cases
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Chip label="Traveler Risk Scoring" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">ML-based passenger targeting</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Chip label="Cargo Anomaly Detection" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Trade fraud identification</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Chip label="Port Performance Analytics" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Wait time and throughput optimization</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Chip label="Officer Productivity Analysis" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Inspection efficiency metrics</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Chip label="Supply Chain Mapping" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Trade network visualization</Typography></Grid>
            <Grid item xs={12} md={4}>
              <Chip label="Fentanyl Interdiction" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Opioid smuggling pattern analysis</Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Container>
  );
};

export default AAPSystemDetail;
